<template>
  <div>
    <!-- mobile view -->
    <div class="pb-6 md:hidden">
      <template v-if="!selectedFolderId">
        <div class="flex items-center justify-between mx-4" v-if="folders.length !== 0">
          <h3 class="font-bold">
            Folders
          </h3>

          <sort-by simplified />
        </div>

        <div class="flex overflow-x-auto flex-nowrap">
          <div
            class="flex-shrink-0 w-40 p-3 mt-4 mb-4 ml-4 rounded shadow-card bg-base-0"
            :key="`folder-${folder.folderId}`"
            v-for="folder in folders"
          >
            <div class="flex justify-between">
              <aha-icon-container
                class="my-3 font-bold truncate cursor-pointer"
                size="lg"
                icon-class="bg-purple-10 text-purple-50"
                @click="toFolderDetails(folder)"
              >
                <aha-icon name="folder" />
              </aha-icon-container>

              <folder-kebab-menu :folder="folder" />
            </div>
            <div
              :title="folder.folderName"
              class="my-3 font-bold truncate cursor-pointer hover:text-primary-90"
              @click="toFolderDetails(folder)"
            >
              {{ folder.folderName }}
            </div>
            <div class="flex items-center">
              <aha-icon-container>
                <aha-icon
                  name="file"
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                />
              </aha-icon-container>
              <span class="text-xs text-base-80">
                {{ folder.presentations.length }}
                presentation{{ folder.presentations.length === 1 ? '' : 's' }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <div class="flex items-center justify-between mx-4" v-if="presentations.length !== 0">
        <h3 class="font-bold">
          Presentations
        </h3>
        <sort-by simplified v-if="folders.length === 0 || selectedFolderId" />
      </div>

      <div
        :key="`presentation-${presentation.id}`"
        v-for="presentation in presentations"
        class="flex p-3 mx-4 my-4 rounded shadow-card bg-base-0"
      >
        <span
          @click="goToPresentationDetail(presentation)"
        >
          <presentation-thumbnail :presentation="presentation" />
        </span>
        <span class="flex flex-col justify-between flex-grow w-0 ml-4">
          <div
            class="font-bold truncate cursor-pointer"
            :title="presentation.name"
            @click="goToPresentationDetail(presentation)"
          >
            <span
              class="text-base-60 text-link"
              v-if="!isSharedPresentation(presentation)"
            >
              {{ getFolderName(presentation) }}</span>
            <span class="text-link">{{ presentation.name }}</span>
          </div>
          <div class="flex justify-between">
            <span class="flex flex-wrap items-center leading-6">
              <PresentationMode :presentation="presentation" class="min-w-max" />

              <span class="hidden ml-4 min-w-max 2xs:inline">
                <aha-icon-container>
                  <aha-icon
                    name="slides"
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  {{ presentation.slideCount }}
                </span>
              </span>

              <span class="hidden ml-4 min-w-max 2xs:inline">
                <aha-icon-container>
                  <aha-icon
                    name="people"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  <aha-formatter
                    :value="presentation.participantsCount || presentation.onlineCount"
                    :locale="presentation.language"
                    type="number"
                  />
                </span>
              </span>

              <Reactions
                class="hidden ml-4 cursor-pointer xs:inline-block"
                @click.stop="showPresentationReport(presentation)"
                :presentation="presentation"
              />
            </span>
          </div>
        </span>
        <presentation-kebab-menu
          class="flex items-center"
          :presentation="presentation"
          :right-click-enabled="false"
        />
      </div>
    </div>

    <!-- desktop view -->
    <table class="hidden main-table md:table md:px-10">
      <thead>
        <tr>
          <table-heading name="checkbox" class="w-1 pl-4 pr-4">
            <aha-checkbox
              :checked="allPresentationSelected"
              :disabled="!presentations.length"
              v-if="isShowCheckAllPresentations"
              @change="handleAllPresentationSelect()"
            />
          </table-heading>
          <table-heading name="thumbnail">
            <span
              class="inline-flex items-center cursor-pointer"
              @click="handleSortPresentations('name')"
            >
              <span class="mr-2">Name</span>
              <sorting-indicator v-if="sortColumn === 'name'" :order="sortOrder" />
            </span>
          </table-heading>
          <table-heading name="name" />
          <table-heading name="accessCode">
            Access Code
          </table-heading>
          <table-heading name="owner">
            Owner
          </table-heading>
          <table-heading name="updatedAt" class="md:w-max">
            <span
              class="inline-flex items-center cursor-pointer md:min-w-max"
              @click="handleSortPresentations('updatedAt')"
            >
              <span class="mr-2">Last Updated</span>
              <sorting-indicator v-if="sortColumn === 'updatedAt'" :order="sortOrder" />
            </span>
          </table-heading>
          <table-heading name="createdAt">
            <span
              class="inline-flex items-center cursor-pointer"
              @click="handleSortPresentations('createdAt')"
            >
              <span class="mr-2">Created</span>
              <sorting-indicator v-if="sortColumn === 'createdAt'" :order="sortOrder" />
            </span>
          </table-heading>
          <table-heading name="presentButton" class="lg:w-16" />
          <table-heading name="kebabMenu" />
        </tr>
      </thead>

      <tbody>
        <!-- desktop folder view -->
        <template v-if="!selectedFolderId">
          <tr
            :key="`folder-${folder.folderId}`"
            v-for="folder in folders"
            @contextmenu.prevent="openKebabMenuFolder(folder, $event)"
          >
            <table-data name="checkbox" class="w-1 pl-4 pr-4" />
            <table-data name="thumbnail" class="main-table--thumbnail">
              <span
                @click="toFolderDetails(folder)"
                class="flex justify-center cursor-pointer"
              >
                <span class="w-20 text-center h-11">
                  <aha-icon-container
                    size="lg"
                    :margin-right="false"
                    icon-class="bg-purple-10 text-purple-50"
                  >
                    <aha-icon name="folder" />
                  </aha-icon-container>
                </span>
              </span>
            </table-data>
            <table-data
              name="name"
              class="main-table--item-name text-link"
              @click="toFolderDetails(folder)"
            >
              <a
                :title="folder.folderName"
                class="text-link"
              >
                {{ folder.folderName }}
              </a>
              <span class="flex flex-wrap items-center mt-1 font-normal leading-6">

                <aha-icon-container>
                  <aha-icon
                    name="file"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  {{ folder.presentations.length }}
                  presentation{{ folder.presentations.length === 1 ? '' : 's' }}
                </span>
              </span>
            </table-data>
            <table-data name="accessCode" class="lg:w-32" />
            <table-data name="owner" class="lg:w-44" />

            <table-data name="updatedAt" class="text-xs cursor-pointer lg:w-36">
              <aha-formatter
                :value="folder.updatedAt"
                type="date"
                showTooltip
                :tooltipValue="folder.updatedAt | dateTime"
              />
            </table-data>
            <table-data name="createdAt" class="text-xs cursor-pointer lg:w-28">
              <aha-formatter
                :value="folder.createdAt"
                type="date"
                showTooltip
                :tooltipValue="folder.createdAt | dateTime"
              />
            </table-data>
            <table-data name="presentButton" class="text-right lg:w-16" />
            <table-data name="kebabMenu" class="text-center lg:w-10">
              <folder-kebab-menu :folder="folder" />
            </table-data>
          </tr>
        </template>

        <!-- desktop presentations list view -->
        <tr
          :key="`presentation-${presentation.id}`"
          v-for="(presentation) in presentations"
          class="table-row"
          @contextmenu.prevent="openKebabMenuPresentation(presentation, $event)"
          :class="{ 'active': presentation.selected }"
        >
          <table-data name="checkbox" class="relative w-1 pl-4 pr-2 main-table--table-cell">
            <div
              class="published-badge"
              v-if="presentation.isPublicSearch"
              v-tooltip="'Published on Template Library'"
            >
              <aha-icon
                width="12"
                height="12"
                fill="white"
                viewBox="0 0 24 24"
                name="inspiration"
                class="published-icon"
              />
            </div>
            <aha-checkbox
              v-if="!isSharedPresentation(presentation)"
              :checked="presentation.selected"
              @change="handlePresentationSelect(presentation)"
            />
          </table-data>
          <table-data
            name="thumbnail"
            class="main-table--thumbnail main-table--table-cell"
            @click="goToPresentationDetail(presentation)"
          >
            <presentation-thumbnail :presentation="presentation" />
          </table-data>
          <table-data
            name="name"
            class="main-table--item-name text-link main-table--table-cell min-w-44"
            @click.stop="goToPresentationDetail(presentation)"
          >
            <PresentationName
              :presentation="presentation"
              :folder-name="getFolderName(presentation)"
              :is-shared-presentation="isSharedPresentation(presentation)"
            />
          </table-data>
          <table-data
            name="accessCode"
            class="text-xs lg:w-32 main-table--table-cell"
          >
            <access-code-editor
              :presentation="presentation"
              @updateAccessCode="updateAccessCode"
            />
          </table-data>
          <table-data name="owner" class="lg:w-44 main-table--table-cell">
            <presentation-owner :presentation="presentation" />
          </table-data>
          <table-data
            name="updatedAt"
            class="text-xs cursor-pointer lg:w-36 main-table--table-cell"
          >
            <aha-formatter
              :value="presentation.updatedAt"
              type="date"
              showTooltip
              :tooltipValue="presentation.updatedAt | dateTime"
            />
          </table-data>
          <table-data
            name="createdAt"
            class="text-xs cursor-pointer lg:w-28 main-table--table-cell"
          >
            <aha-formatter
              :value="presentation.createdAt"
              type="date"
              showTooltip
              :tooltipValue="presentation.createdAt | dateTime"
            />
          </table-data>
          <table-data name="presentButton" class="text-right lg:w-16 main-table--table-cell">
            <present-button :presentation="presentation" class="opacity-0" />
          </table-data>
          <table-data name="kebabMenu" class="text-center lg:w-10 main-table--table-cell">
            <presentation-kebab-menu :presentation="presentation" />
          </table-data>
        </tr>
      </tbody>
    </table>

    <div class="text-center" v-if="morePresentationsLoading">
      <aha-spinner />
    </div>

    <modal-view-only-warning
      @hide="handleHideViewOnlyWarningModal"
      @view="handleClickView"
      v-if="isShowingViewOnlyWarningModal"
      :active-editor="activeEditor"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import EventBus from '@/eventBus'

import Utils from '@/util'
import constants from '@/constant'
import socketHelper from '@/helpers/socket/socket-helper'

// modals
import ModalViewOnlyWarning from '@/components-v2/modals/ViewOnlyWarning.vue'

// components
import PresentationName from '@/components-v2/my-presentations/main-table/PresentationName.vue'
import Reactions from '@/components-v2/my-presentations/main-table/Reactions.vue'
import FolderKebabMenu from './FolderKebabMenu.vue'
import PresentButton from './PresentButton.vue'
import PresentationThumbnail from './PresentationThumbnail.vue'
import PresentationKebabMenu from './PresentationKebabMenu.vue'
import PresentationMode from './PresentationMode.vue'
import SortBy from '../SortBy.vue'
import SortingIndicator from './SortingIndicator.vue'
import TableData from './TableData.vue'
import TableHeading from './TableHeading.vue'
import AccessCodeEditor from './AccessCodeEditor.vue'
import PresentationOwner from './PresentationOwner.vue'

export default {
  components: {
    Reactions,
    FolderKebabMenu,
    PresentationKebabMenu,
    PresentationName,
    PresentationMode,
    PresentationThumbnail,
    PresentButton,
    SortBy,
    SortingIndicator,
    TableData,
    TableHeading,
    AccessCodeEditor,
    PresentationOwner,
    ModalViewOnlyWarning,
  },
  data() {
    return {
      areLoadingDefaultPresentations: false,
      presentationsAndFoldersLoading: false,
      isShowingViewOnlyWarningModal: false,
      isClaimingFreeOneTimeOffer: false,
      isDecliningFreeOneTimeOffer: false,
      isShowingWrapup: true,
      activePresentation: null,
      activeEditor: null,
    }
  },
  computed: {
    allPresentationSelected() {
      const visiblePresentations = this.presentations.filter((presentation) => {
        const { folderId, userId } = presentation

        return folderId === this.selectedFolderId && userId === this.currentUser.id
      })

      if (visiblePresentations.length === 0) return false

      return visiblePresentations.every((presentation) => presentation.selected)
    },
    ...mapState({
      folders: (state) => state.folders,
      morePresentationsLoading: (state) => state.loaders.morePresentationsLoading,
      selectedFolderId: (state) => state.selections.selectedFolderId,
      presentations: (state) => state.presentations,
      isServerConnectable: (state) => state.indicators.isServerConnectable,
      selectedSortCriteria: (state) => state.selections.selectedSortCriteria,
      sortColumn: (state) => state.selections.selectedSortCriteria.sortColumn,
      sortOrder: (state) => state.selections.selectedSortCriteria.sortOrder,
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters(['selectedFolder']),
    isShowCheckAllPresentations() {
      if (this.$route.name === 'SharedWithMe') return false
      return true
    },

    computedShowFreeOneTimeOffer() {
      // when to display banner:
      // 1. signed up after deployment date in May
      // 2. has created at least one presentation
      // 3. claimed free offer, not declined
      // 4. not used or still in active period
      // 5. current active plan is one-time medium (free)
      // 6. not white label || skoletube
      // 7. A/B group 1

      const {
        createdAt,
        personalInformation,
        oneTimeEnd,
        oneTimeFreeOffer,
        plan,
        id,
      } = this.currentUser
      const freeOneTimeStatusClaimed = personalInformation && personalInformation.oneTimeOfferStatus === 'oneTimeOfferStatusClaimed'
      return freeOneTimeStatusClaimed
        && new Date(createdAt) >= new Date('2022-05-15')
        && this.presentations.length
        && (!oneTimeEnd || parseInt(oneTimeEnd, 10) > Date.now())
        && oneTimeFreeOffer
        && plan === 'one-time-medium'
        && !Utils.checkWhiteLabelDomain() && this.currentUser.identifyProvider !== 'skoletube'
        && id % 4 === 1
    },

    currentUserName() {
      if (!this.currentUser ||
        (!this.currentUser.firstName && !this.currentUser.lastName)) return false
      return `${this.currentUser.firstName} ${this.currentUser.lastName}`
    },
  },
  mounted() {
    this.handleDisplayViewOnlyWarning()
  },
  methods: {
    ...mapActions('presentationReport', ['selectPresentationAction', 'generateReportAction', 'subscribeReportAction', 'openReportPanelAction']),
    ...mapActions([
      'setSlideActiveAction',
      'setPresentationsAction',
      'setSelectedSortCriteriaAction',
      'setCurrentUserAction',
    ]),

    showPresentationReport(presentation) {
      this.selectPresentationAction({
        presentation,
      })
      if (presentation && presentation.uniqueAccessCode) {
        socketHelper.joinRoomPresentation(presentation.uniqueAccessCode)
        this.subscribeReportAction(presentation)
      }
      this.openReportPanelAction()
      Utils.gaTrackEvent(constants.gaCategories.PRESENTATION_LIST, 'Presentation > Click view report')
    },
    handleHideViewOnlyWarningModal() {
      this.isShowingViewOnlyWarningModal = null
    },
    handleClickView() {
      this.goToPresentationDetail(this.activePresentation)
    },
    handleDisplayViewOnlyWarning() {
      EventBus.$on('modal-view-only:before-perform-check', (presentation) => {
        this.activePresentation = presentation
      })
      EventBus.$on('modal-view-only:after-perform-check', ({ hasPerm, activeEditor }) => {
        if (!hasPerm) {
          this.isShowingViewOnlyWarningModal = true
          const { firstName, lastName } = activeEditor
          activeEditor.name = lastName ? `${firstName} ${lastName}` : firstName
          this.activeEditor = activeEditor
        }
      })
    },
    closeOtherMenu() {
      document.body.click()
    },
    openKebabMenuPresentation(presentation, event) {
      this.closeOtherMenu()
      const { x, y } = event
      EventBus.$emit('open-kebab-presentation', { x, y, id: presentation.id })
    },
    openKebabMenuFolder(folder, event) {
      this.closeOtherMenu()
      const { x, y } = event
      EventBus.$emit('open-kebab-folder', { x, y, id: folder.folderId })
    },
    getFolderName(presentation) {
      const { q: searchValue } = this.$route.query

      if (!searchValue) return null

      if (!presentation.folder) return null

      return `${presentation.folder.folderName} / `
    },
    handlePresentationSelect(changedPresentation) {
      const newPresentations = this.presentations.map((presentation) => {
        if (presentation.id === changedPresentation.id) {
          return { ...presentation, selected: !presentation.selected }
        }

        return presentation
      })

      this.setPresentationsAction(newPresentations)
    },
    async goToPresentationDetail(item) {
      this.setSlideActiveAction({ index: 0 })

      const query = {}
      const params = { id: item.id }

      if (this.$store.state.isAdmin) {
        query.admin = true
      }

      this.$router.push({ name: 'PresentationDetail', params, query })
    },
    handleAllPresentationSelect() {
      const newPresentations = this.presentations
        .map((presentation) => {
          const { folderId, userId } = presentation

          if (folderId === this.selectedFolderId && userId === this.currentUser.id) {
            return { ...presentation, selected: !this.allPresentationSelected }
          }

          return presentation
        })

      this.setPresentationsAction(newPresentations)
    },
    async toFolderDetails(folder) {
      this.$router.push({
        name: 'FolderDetail',
        params: {
          folderId: folder.folderId,
        },
      })
    },
    handleSortPresentations(sortColumn) {
      let sortOrder = 'desc'

      if (sortColumn === this.sortColumn && this.sortOrder === 'desc') {
        sortOrder = 'asc'
      }

      this.setSelectedSortCriteriaAction({
        sortColumn,
        sortOrder,
      })
    },
    async updateAccessCode(updatedItem) {
      const newPresentations = this.presentations.map((item) => {
        if (item.id === updatedItem.id) {
          return { ...item, accessCode: updatedItem.newAccessCode.toUpperCase() }
        }

        return item
      })
      this.setPresentationsAction(newPresentations)
    },
    isSharedPresentation(presentation) {
      return presentation.userId !== this.currentUser.id
    },
    openOneTimePage() {
      window.open('https://help.ahaslides.com/portal/en/kb/articles/one-time-plans', '_blank')
      Utils.gaTrackEvent(constants.gaCategories.PRESENTATION_LIST, 'Open free one-time details', 'One-time medium')
    },
  },
}
</script>

<style lang="scss">
.main-table {
  @apply border-separate w-full;
  border-spacing: 0 1rem;
}

.main-table tbody tr {
  @apply rounded-lg text-base-80 shadow-card;

  &:hover {
    box-shadow: 0px 25px 20px -20px rgba(0, 0, 0, .2);

    .report-button {
      @media screen and (min-width:1100px) {
        @apply flex;
      }
    }
    .present-button {
      opacity: 100;
    }
  }

  &.active {
    @apply ring-1;

    &:hover {
      @apply ring-1;
    }
  }

  transition: .5s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
}

.main-table td.main-table--table-cell:first-child {
  @apply rounded-l-lg border-l;
}

.main-table td.main-table--table-cell:last-child {
  @apply rounded-r-lg border-r;
}

.main-table--thumbnail {
  @apply w-20 relative;
}

.main-table--item-name {
  @apply ml-4 font-bold truncate transition-colors max-w-48;
  &:hover > a {
    @apply text-primary-100;
  }
}

.published-icon {
  position: relative;
  top: 0rem;
  left: 0.25rem;
}

.published-badge {
  color: white;
  background: linear-gradient(to bottom right,#33C173 50%,white 50%);
  border-radius: 0.5rem 0rem;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: -1px;
  left: -1px;
}
</style>
